import { clsx } from 'clsx';
import { ReactNode } from 'react';
import i18n from 'helpers/i18n';
import messages from './messages';
import { IconCheck, IconError } from 'components/icons';
import { SectionWithBorder } from 'components/Measure/View/Styled';

import {
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionTrigger,
  AccordionContent
} from '@radix-ui/react-accordion';

interface QuestionAccordionProps {
  /** Question ids of the currently expanded questions. */
  value: string[];

  /** Callback fired when questions are expanded or collapsed. */
  onValueChange: (value: string[]) => void;

  /** `QuestionAccordionItem` list. */
  children: ReactNode;
}

export function QuestionAccordion({
  value,
  onValueChange,
  children
}: QuestionAccordionProps) {
  return (
    <Accordion
      type="multiple"
      value={value}
      onValueChange={onValueChange}
      className="flex flex-col gap-2"
    >
      {children}
    </Accordion>
  );
}

interface QuestionAccordionItemProps {
  /** Question ID. Identifies the item within the accordion. */
  questionId: number;

  /** Question text. Becomes the accessible name of the trigger. */
  question: string;

  /** When `true`, the question is shown as answered. */
  isAnswered: boolean;

  /**
   * When `true`, the question is outlined as invalid. Pair it with an `error`
   * on the answer field so the reason is available to everyone, not just to
   * users who can see the outline.
   */
  error?: boolean;

  /** Additional classes for the question's heading text. */
  headingClassName?: string;

  /** Overrides the expanded content's default `pt-1` top padding. */
  contentClassName?: string;

  /** Answer field. */
  children: ReactNode;
}

export function QuestionAccordionItem({
  questionId,
  question,
  isAnswered,
  error,
  headingClassName,
  contentClassName,
  children
}: QuestionAccordionItemProps) {
  return (
    <SectionWithBorder
      error={error}
      className="transition-shadow duration-200 hover:shadow-md"
    >
      <AccordionItem value={String(questionId)}>
        {/*
          Radix renders this as an `h3`, which keeps the questions navigable as
          headings. The globally imported semantic-ui stylesheet styles bare
          headings, so the typography is reset back to the surrounding text.
        */}
        <AccordionHeader className="m-0 font-normal text-[length:inherit] leading-[inherit]">
          <AccordionTrigger
            className={clsx(
              'w-full flex items-center gap-3 py-2 text-left cursor-pointer',
              'rounded focus-visible:outline-none focus-visible:ring-2',
              'focus-visible:ring-scale-blue focus-visible:ring-offset-2'
            )}
          >
            <span className={clsx('grow', headingClassName)}>{question}</span>

            <span className="shrink-0 flex items-center gap-2 rounded-xl bg-white p-2 sm:px-4">
              {isAnswered
                ? i18n.ft(messages.answered)
                : i18n.ft(messages.notAnswered)}

              {/*
                Blank keeps an empty slot of the same size so questions stay
                aligned whichever state they are in.
              */}
              {isAnswered ? (
                <IconCheck size="xl" />
              ) : error ? (
                <IconError size="xl" />
              ) : (
                <span
                  aria-hidden="true"
                  className="h-5 w-5 rounded-full border border-scale-neutral-200"
                />
              )}
            </span>
          </AccordionTrigger>
        </AccordionHeader>

        <AccordionContent className={clsx('pb-3', contentClassName ?? 'pt-1')}>
          {children}
        </AccordionContent>
      </AccordionItem>
    </SectionWithBorder>
  );
}
